/**
 * Redimensiona y comprime imágenes en el cliente antes de subirlas a Storage.
 *
 * Motivación: los avatares se servían como el archivo original (hasta 5 MB)
 * y se mostraban en cards de ~200-260px. Bajar un JPEG de varios MB para
 * pintarlo chico genera un delay innecesario en la carga del hero. Al
 * redimensionar a un máximo razonable y re-codificar a WebP, el archivo
 * guardado pasa de MBs a decenas de KB — funciona en cualquier plan de
 * Supabase (no depende de la transformación on-the-fly, que es de pago).
 *
 * Estrategia defensiva: si algo falla (canvas no disponible, WebP no
 * soportado, o el resultado pesa más que el original) devolvemos el archivo
 * original sin tocar. Nunca rompemos la subida por intentar optimizar.
 */

export interface ResizeOptions {
  /** Lado máximo (px). La imagen se escala manteniendo el aspect ratio. */
  maxDimension?: number;
  /** Calidad de compresión WebP (0–1). */
  quality?: number;
  /** Mime de salida. WebP da el mejor ratio tamaño/calidad. */
  outputType?: 'image/webp' | 'image/jpeg';
}

const DEFAULTS: Required<ResizeOptions> = {
  // 512px cubre todos los usos actuales (cards del hero ~260px, avatar del
  // dashboard, etc.) incluso en pantallas retina (2x).
  maxDimension: 512,
  quality: 0.82,
  outputType: 'image/webp',
};

/**
 * Devuelve un File optimizado (redimensionado + comprimido). Si no se puede
 * optimizar de forma segura, devuelve el `file` original sin cambios.
 */
export async function resizeImageFile(
  file: File,
  options: ResizeOptions = {},
): Promise<File> {
  const opts = { ...DEFAULTS, ...options };

  // Solo procesamos imágenes rasterizadas. SVG/gif u otros se dejan igual.
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || file.type === 'image/gif') {
    return file;
  }

  try {
    const bitmap = await loadBitmap(file);
    const { width, height } = fitWithin(bitmap.width, bitmap.height, opts.maxDimension);

    // Si la imagen ya es más chica que el máximo y pesa poco, no vale la pena
    // re-codificar (evita degradar calidad sin beneficio de tamaño).
    const alreadySmall =
      bitmap.width <= opts.maxDimension &&
      bitmap.height <= opts.maxDimension &&
      file.size <= 200 * 1024;
    if (alreadySmall) {
      closeBitmap(bitmap);
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      closeBitmap(bitmap);
      return file;
    }
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap.img, 0, 0, width, height);
    closeBitmap(bitmap);

    const blob = await canvasToBlob(canvas, opts.outputType, opts.quality);
    // Si por algún motivo el resultado no es más chico, nos quedamos con el original.
    if (!blob || blob.size >= file.size) {
      return file;
    }

    const ext = opts.outputType === 'image/webp' ? 'webp' : 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.${ext}`, {
      type: opts.outputType,
      lastModified: Date.now(),
    });
  } catch {
    // Cualquier fallo → subimos el original sin optimizar.
    return file;
  }
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

type DecodedImage =
  | { kind: 'bitmap'; img: ImageBitmap; width: number; height: number }
  | { kind: 'element'; img: HTMLImageElement; url: string; width: number; height: number };

async function loadBitmap(file: File): Promise<DecodedImage> {
  // createImageBitmap es el camino rápido y respeta orientación en navegadores
  // modernos. Si no existe, caemos a un <img> con object URL.
  if (typeof createImageBitmap === 'function') {
    try {
      const img = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return { kind: 'bitmap', img, width: img.width, height: img.height };
    } catch {
      // sigue al fallback
    }
  }
  const url = URL.createObjectURL(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('No se pudo decodificar la imagen'));
    el.src = url;
  });
  return { kind: 'element', img, url, width: img.naturalWidth, height: img.naturalHeight };
}

function closeBitmap(decoded: DecodedImage) {
  if (decoded.kind === 'bitmap') {
    decoded.img.close?.();
  } else {
    URL.revokeObjectURL(decoded.url);
  }
}

function fitWithin(w: number, h: number, max: number): { width: number; height: number } {
  if (w <= max && h <= max) return { width: w, height: h };
  const scale = Math.min(max / w, max / h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}
